import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Loader2, Send } from "lucide-react";

/**
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - selectedCount: number
 * - emailAddress: string
 * - setEmailAddress: (v: string) => void
 * - emailMessage: string
 * - setEmailMessage: (v: string) => void
 * - emailSending: boolean
 * - onSend: () => void
 * - onCancel: () => void
 */
export function EmailModal({
  open,
  onOpenChange,
  selectedCount,
  emailAddress,
  setEmailAddress,
  emailMessage,
  setEmailMessage,
  emailSending,
  onSend,
  onCancel,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="text-purple-600" size={28} />
            Send Venue List via Email
          </DialogTitle>
          <DialogDescription>
            Send {selectedCount} selected venues as a CSV attachment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <label
              htmlFor="email-address"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Recipient Email Address *
            </label>
            <Input
              id="email-address"
              type="email"
              placeholder="example@email.com"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              disabled={emailSending}
              className="w-full"
              required
            />
          </div>

          <div>
            <label
              htmlFor="email-message"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Additional Message (Optional)
            </label>
            <textarea
              id="email-message"
              placeholder="Add a note to include in the email..."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              disabled={emailSending}
              className="w-full min-h-[100px] p-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
            />
          </div>

          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-900 mb-2">What's Included:</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>✓ {selectedCount} selected venue(s)</li>
              <li>✓ CSV file with complete venue details</li>
              <li>✓ Venue names, addresses, coordinates</li>
              <li>✓ Venue types and contact information</li>
            </ul>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                onCancel?.();
                onOpenChange(false);
              }}
              disabled={emailSending}
              className="flex-1"
            >
              Cancel
            </Button>

            <Button
              onClick={onSend}
              disabled={emailSending || !emailAddress}
              className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              {emailSending ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2" size={16} />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}